import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sprintf } from "sprintf-js";
import { AppBar, Box, Button, Container, createTheme, CssBaseline, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Fab, IconButton, List, ListItem, ListItemButton, ListItemText, Snackbar, styled, ThemeProvider, Toolbar, Typography, useMediaQuery } from "@mui/material";
import { teal } from "@mui/material/colors";
import { ArrowBack, Delete } from "@mui/icons-material";
import CalculatorPortrait from "./CalculatorPortrait";
import CalculatorLandscape from "./CalculatorLandscape";
import CalculatorEditor from "./CalculatorEditor";
import CalculatorResult from "./CalculatorResult";
import { jsclMsgs, msgs } from "./CalculatorL10n";
import { useWorker } from "./CalculatorWorker";
import DragButton from "./DragButton";
import { WORKER_VERSION } from "./worker_types";
import { bgSx, InlineDiv, ScrollableFilledBox } from "./CalculatorStyled";
import { CalculatorFunctionSelect, CalculatorVariableSelect } from "./CalculatorSelect";
import CalculatorIntro from "./CalculatorIntro";
const { floor, min, max } = Math;
const DEFAULT_FONT_SIZE = 32;
const invokeDelayed = setTimeout;
const clearDelayed = clearTimeout;
const copyText = (str) => {
    const D = document, element = D.createElement("input");
    element.style.opacity = "0";
    element.value = str;
    D.body.appendChild(element);
    element.select();
    D.execCommand("copy");
    element.remove();
};
const themeBase = createTheme({ palette: { mode: "light" } });
const themeDarkBase = createTheme({ palette: { mode: "dark" } });
const themeLight = createTheme(themeBase, {
    palette: {
        mode: "light",
        primary: themeBase.palette.augmentColor({
            color: {
                main: teal[500],
            },
        }),
        background: {
            default: "#f5f5f5",
            paper: "#f5f5f5",
        },
        normalButton: "#424242",
        darkButton: "#313131",
        primaryButton: teal[400],
        primaryDarkButton: teal[500],
    },
});
const themeDark = createTheme(themeDarkBase, {
    palette: {
        mode: "dark",
        primary: themeDarkBase.palette.augmentColor({
            color: {
                main: "#1156b1",
            },
        }),
        background: {
            default: "#101010",
            paper: "#101010",
        },
        normalButton: "#212121",
        darkButton: "#313131",
        primaryButton: "#0d47a1",
        primaryDarkButton: "#1156b1",
    },
});
const FilledBox = styled(Box)({
    width: "100%",
    height: "100%",
});
const CenterContainer = styled(Container)({
    textAlign: "center",
});
const BottomFab = styled(Fab)({
    position: "absolute",
    bottom: 16,
    right: 16,
});
const snackbarOrigin = {
    horizontal: "center",
    vertical: "bottom",
};
const wizardStringMap = {
    [0 /* WizardState.CENTER */]: 8 /* S.cpp_wizard_dragbutton_action_center */,
    [1 /* WizardState.UP */]: 9 /* S.cpp_wizard_dragbutton_action_up */,
    [2 /* WizardState.DOWN */]: 10 /* S.cpp_wizard_dragbutton_action_down */,
    [3 /* WizardState.END */]: 11 /* S.cpp_wizard_dragbutton_action_end */,
};
const useCalculatorSize = () => {
    const [landscape, setLandscape] = useState(false);
    const [buttonSize, setButtonSize] = useState(DEFAULT_FONT_SIZE * 2);
    const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);
    const [container, setContainer] = useState(null);
    useEffect(() => {
        if (container) {
            {
                const initialRect = container.getBoundingClientRect();
                setLandscape(initialRect.width > initialRect.height);
            }
            const observer = new ResizeObserver(entries => {
                const contentRect = entries[0].contentRect;
                const newLandscape = contentRect.width > contentRect.height;
                const buttonSize = contentRect.height / (newLandscape ? 6 : 8);
                setLandscape(newLandscape);
                setButtonSize(buttonSize);
                setFontSize(floor(buttonSize / 2));
            });
            observer.observe(container);
            return () => {
                observer.disconnect();
            };
        }
    }, [container]);
    return [buttonSize, fontSize, landscape, setContainer];
};
const AppWizard = memo(({ buttonSize, fontSize, setPage }) => {
    const [wizardState, setWizardState] = useState(0 /* WizardState.CENTER */);
    return (_jsxs(CenterContainer, { children: [_jsx("br", {}), _jsx("p", { children: msgs[7 /* S.cpp_wizard_dragbutton_description */] }), _jsx("br", {}), _jsx("p", { children: msgs[wizardStringMap[wizardState]] }), _jsx("br", {}), _jsx("div", { style: useMemo(() => ({
                    display: "inline-block",
                    width: buttonSize,
                    height: buttonSize,
                }), [buttonSize]), children: _jsx(DragButton, { bgColor: "primaryButton", fontSize: fontSize, text: "9", textUp: "%", textDown: "^2", onClick: useCallback(() => {
                        if (wizardState === 0 /* WizardState.CENTER */)
                            setWizardState(1 /* WizardState.UP */);
                        else if (wizardState === 3 /* WizardState.END */)
                            setWizardState(0 /* WizardState.CENTER */);
                    }, [wizardState]), onUpDrag: useCallback(() => {
                        if (wizardState === 1 /* WizardState.UP */)
                            setWizardState(2 /* WizardState.DOWN */);
                    }, [wizardState]), onDownDrag: useCallback(() => {
                        if (wizardState === 2 /* WizardState.DOWN */)
                            setWizardState(3 /* WizardState.END */);
                    }, [wizardState]) }) }), _jsx("br", {}), _jsx("br", {}), _jsx(InlineDiv, { children: _jsx(Button, { onClick: useCallback(() => setPage(2 /* Page.MAIN */), []), variant: "contained", children: msgs[6 /* S.cpp_wizard_finish */] }) })] }));
});
const CalculatorHistory = memo(({ historyItems, enterHistory, clearHistory, exit }) => {
    const reversedHistoryItems = useMemo(() => [...historyItems].reverse(), [historyItems]);
    return (_jsxs(_Fragment, { children: [_jsx(AppBar, { children: _jsxs(Toolbar, { children: [_jsx(IconButton, { size: "large", color: "inherit", edge: "start", onClick: exit, children: _jsx(ArrowBack, {}) }), _jsx(Typography, { variant: "h6", component: "div", children: msgs[22 /* S.c_history */] })] }) }), _jsx(ScrollableFilledBox, { children: _jsx(Container, { sx: bgSx, children: _jsxs(List, { children: [_jsx(Toolbar, {}), reversedHistoryItems.map((item) => (_jsx(ListItem, { disablePadding: true, children: _jsx(ListItemButton, { onClick: useCallback(() => enterHistory(item), [enterHistory]), component: "button", children: _jsx(ListItemText, { primary: item[1] + item[2] + item[3] }) }) }, item[0])))] }) }) }), _jsx(BottomFab, { color: "primary", onClick: clearHistory, children: _jsx(Delete, {}) })] }));
});
const Calculator = memo(({ landscape, fontSize, workerState, workerMessageRef, historyItems, page, setPage }) => {
    const textAreaRef = useRef(null);
    const textAreaHasFocus = useRef(false);
    const textAreaValueForRender = useRef("");
    const resultTextRef = useRef(null);
    const [resultNotReady, setResultNotReady] = useState(true);
    const [needReplaceResult, setNeedReplaceResult] = useState(true);
    const [result, setResult] = useState("");
    const lastCursorPos = useRef(0);
    // lastCursorRange is never used in cpp
    const lastCursorRange = useMemo(() => ({ start: 0, end: 0 }), []);
    const workerBusy = useRef(false);
    const calcUid = useRef(0);
    const [workerRef, workerLoadError, reInitWorker, setWorkerError] = workerState;
    const [angleUnit, setAngleUnit] = useState("rad" /* AngleUnit.rad */);
    const [numeralBase, setNumeralBase] = useState("dec" /* NumeralBase.dec */);
    const lastCalculateType = useRef(null);
    const calcWithHistory = useRef(0 /* CalcHistoryOption.ADD_HISTORY */);
    const historyPtr = useRef(-1);
    const variableToEnter = useRef(null);
    const functionToEnter = useRef(null);
    const pendingWorkerRequest = useRef(null);
    const workerDelayTimeout = useRef(null);
    const [snackbarMessage, setSnackbarMessage] = useState(null);
    const snackbarHideTimeout = useRef(-1);
    const memoryExpr = useRef(["0", "deg" /* AngleUnit.deg */, "dec" /* NumeralBase.dec */]);
    const postWorkerMessage = useCallback((msg) => {
        if (workerRef.current) {
            workerRef.current.postMessage(msg);
        }
    }, [workerRef]);
    const postWorkerMessageDelayed = useCallback((msg) => {
        if (workerBusy.current) {
            pendingWorkerRequest.current = msg;
            if (workerDelayTimeout.current === null) {
                workerDelayTimeout.current = invokeDelayed(delayedReInitWorker, 500);
            }
        }
        else {
            workerBusy.current = true;
            postWorkerMessage(msg);
        }
    }, [workerRef]);
    const nextUid = useCallback(() => {
        return calcUid.current = (calcUid.current + 1) & 0xffffffff;
    }, [calcUid]);
    const setTextRef = useCallback((textArea) => {
        const isAttach = textAreaRef.current === null && textArea !== null;
        textAreaRef.current = textArea;
        if (isAttach) {
            textArea.selectionStart = textArea.selectionEnd = lastCursorPos.current;
            if (variableToEnter.current !== null) {
                insertString(variableToEnter.current);
                variableToEnter.current = null;
            }
            else if (functionToEnter.current !== null) {
                insertFun(functionToEnter.current);
                functionToEnter.current = null;
            }
            else {
                textArea.focus();
            }
        }
    }, [textAreaRef]);
    const onSelectionChange = useCallback(() => {
        const selection = document.getSelection();
        const textArea = textAreaRef.current;
        if (selection && textArea && textAreaHasFocus.current) {
            const startPos = textArea.selectionStart, endPos = textArea.selectionEnd;
            if (startPos === endPos) {
                lastCursorPos.current = startPos;
            }
            else {
                lastCursorRange.start = startPos;
                lastCursorRange.end = endPos;
            }
        }
    }, []);
    const addHistoryItem = useCallback((item) => {
        if (historyItems.length === 0 && calcWithHistory.current === 1 /* CalcHistoryOption.REPLACE_HISTORY */) {
            calcWithHistory.current = 0 /* CalcHistoryOption.ADD_HISTORY */;
        }
        switch (calcWithHistory.current) {
            case 0 /* CalcHistoryOption.ADD_HISTORY */:
                historyItems.push(item);
                break;
            case 1 /* CalcHistoryOption.REPLACE_HISTORY */:
                historyItems[historyItems.length - 1] = item;
                break;
        }
        historyPtr.current = historyItems.length - 1;
    }, []);
    const closeSnackbar = useCallback(() => setSnackbarMessage(null), []);
    const showSnackbar = useCallback((msg) => {
        clearDelayed(snackbarHideTimeout.current);
        setSnackbarMessage(msg);
        snackbarHideTimeout.current = invokeDelayed(closeSnackbar, 2000);
    }, []);
    workerMessageRef.current = useCallback((e) => {
        if (workerDelayTimeout.current !== null) {
            clearDelayed(workerDelayTimeout.current);
            workerDelayTimeout.current = null;
            if (pendingWorkerRequest.current !== null) {
                const request = pendingWorkerRequest.current;
                pendingWorkerRequest.current = null;
                workerBusy.current = true;
                postWorkerMessage(request);
                return;
            }
        }
        const result = e.data;
        switch (result.type) {
            case "init":
                if (result.version !== WORKER_VERSION) {
                    setWorkerError("Invalid worker version '" + result.version + "'. Try to clear browser cache.");
                }
                break;
            case "eval/simp" /* RequestType.EVALUATE_OR_SIMPLIFY */:
            case "evaluate" /* RequestType.EVALUATE */:
            case "simplify" /* RequestType.SIMPLIFY */:
                if (result.uid === calcUid.current) {
                    workerBusy.current = false;
                    lastCalculateType.current = result.type;
                    const equalChar = (result.success && (result.resultType || result.type) === "simplify" /* RequestType.SIMPLIFY */) ? "≡" : "=";
                    if (result.success) {
                        setResultNotReady(false);
                        setNeedReplaceResult(false);
                        setResult(result.result);
                        addHistoryItem([result.uid, result.expr, equalChar, result.result, result.expr.length, false]);
                    }
                    else {
                        setResultNotReady(true);
                        setNeedReplaceResult(true);
                        const error = result.simplifyError || result.evaluateError;
                        let formattedError;
                        let errorPos = result.expr.length;
                        if (typeof error === "string") {
                            formattedError = String(error);
                        }
                        else {
                            try {
                                formattedError = sprintf(jsclMsgs[error.messageCode], ...error.params);
                                errorPos = Number(error.position);
                            }
                            catch (ignored) {
                                formattedError = String(error);
                            }
                        }
                        setResult(formattedError);
                        addHistoryItem([result.uid, result.expr, equalChar, formattedError, errorPos, true]);
                    }
                }
                break;
            case "addMem" /* RequestType.ADD_MEMORY */:
            case "subMem" /* RequestType.SUB_MEMORY */:
            case "clrMem" /* RequestType.CLEAR_MEMORY */:
                if (result.uid === calcUid.current) {
                    workerBusy.current = false;
                    if (result.success) {
                        memoryExpr.current = [result.result, angleUnit, numeralBase];
                        showSnackbar(result.result);
                    }
                }
                break;
            case "getMem" /* RequestType.GET_MEMORY */:
                if (result.uid === calcUid.current) {
                    workerBusy.current = false;
                    if (result.success) {
                        memoryExpr.current = [result.result, angleUnit, numeralBase];
                        insertString(result.result);
                    }
                }
                break;
            default:
                if (result.uid === calcUid.current) {
                    workerBusy.current = false;
                }
                break;
        }
    }, [angleUnit, numeralBase]);
    const delayedReInitWorker = useCallback(() => {
        if (workerDelayTimeout.current !== null) {
            clearDelayed(workerDelayTimeout.current);
            workerDelayTimeout.current = null;
            reInitWorker(memoryExpr.current);
            workerBusy.current = false;
            if (pendingWorkerRequest.current !== null) {
                const request = pendingWorkerRequest.current;
                pendingWorkerRequest.current = null;
                workerBusy.current = true;
                postWorkerMessage(request);
            }
        }
    }, []);
    const startCalculation = useCallback((type, historyOption) => {
        const textArea = textAreaRef.current;
        const expr = textArea ? textArea.value : textAreaValueForRender.current;
        textAreaValueForRender.current = expr;
        calcWithHistory.current = (historyOption === undefined) ? 0 /* CalcHistoryOption.ADD_HISTORY */ : historyOption;
        if (expr) {
            if (!resultNotReady && lastCalculateType.current !== null && type === "evaluate" /* RequestType.EVALUATE */) {
                textAreaValueForRender.current = result;
                if (textArea)
                    textArea.value = result;
                moveCursor(3 /* MoveCursor.END */);
                return;
            }
            setResultNotReady(true);
            setNeedReplaceResult(false);
            lastCalculateType.current = null;
            const uid = nextUid();
            const request = {
                type: type,
                uid: uid,
                expr: expr,
                angleUnit: angleUnit,
                numeralBase: numeralBase,
            };
            postWorkerMessageDelayed(request);
        }
        else {
            setResultNotReady(false);
            setNeedReplaceResult(false);
            setResult("");
        }
    }, [angleUnit, numeralBase]);
    const handleTextChange = useCallback(() => startCalculation("eval/simp" /* RequestType.EVALUATE_OR_SIMPLIFY */, needReplaceResult ? 1 /* CalcHistoryOption.REPLACE_HISTORY */ : 0 /* CalcHistoryOption.ADD_HISTORY */), [startCalculation, needReplaceResult]);
    const insertString = useCallback((str, off) => {
        if (workerRef.current === null)
            return;
        const textArea = textAreaRef.current;
        const currentValue = textArea.value;
        const newValue = currentValue.substring(0, lastCursorPos.current) + str + currentValue.substring(lastCursorPos.current, currentValue.length);
        if (newValue !== currentValue) {
            const newCursorPos = lastCursorPos.current + str.length + (off || 0);
            textArea.value = newValue;
            lastCursorPos.current = textArea.selectionStart = textArea.selectionEnd = newCursorPos;
        }
        textArea.focus();
        handleTextChange();
    }, [handleTextChange]);
    const insertFun = useCallback((fun) => {
        switch (fun) {
            case "∫":
            case "∂":
                insertString(fun + "(,x)", -3);
                break;
            case "Σ":
            case "∏":
                insertString(fun + "(,i,a,b)", -7);
                break;
            case "∫ab":
                insertString("∫ab(,x,a,b)", -7);
                break;
            case "mod":
            case "log":
                insertString(fun + "(,)", -2);
                break;
            case "x!":
            case "x%":
            case "x°":
            case "x!!":
                insertString(fun.substring(1));
                break;
            default:
                insertString(fun + "()", -1);
                break;
        }
    }, [insertString]);
    const moveCursor = useCallback((mv) => {
        const textArea = textAreaRef.current;
        const valueLen = textArea.value.length;
        let cursorPos = lastCursorPos.current;
        switch (mv) {
            case 0 /* MoveCursor.LEFT */:
                cursorPos = max(0, cursorPos - 1);
                break;
            case 1 /* MoveCursor.RIGHT */:
                cursorPos = min(cursorPos + 1, valueLen);
                break;
            case 2 /* MoveCursor.START */:
                cursorPos = 0;
                break;
            case 3 /* MoveCursor.END */:
                cursorPos = valueLen;
                break;
        }
        lastCursorPos.current = textArea.selectionStart = textArea.selectionEnd = cursorPos;
        textArea.focus();
    }, []);
    const onBracketPair = useCallback((withSpace) => {
        if (!withSpace) {
            insertString("()", -1);
        }
        else {
            // TODO unimplemented
            insertString("()", -1);
        }
    }, [insertString]);
    const erase = useCallback(() => {
        const textArea = textAreaRef.current;
        const currentValue = textArea.value;
        let cursorPos = lastCursorPos.current;
        if (cursorPos > 0) {
            const newValue = currentValue.substring(0, cursorPos - 1) + currentValue.substring(cursorPos, currentValue.length);
            textArea.value = newValue;
            cursorPos--;
            lastCursorPos.current = textArea.selectionStart = textArea.selectionEnd = cursorPos;
        }
        textArea.focus();
        handleTextChange();
    }, [handleTextChange]);
    const clear = useCallback(() => {
        const textArea = textAreaRef.current;
        textArea.value = textAreaValueForRender.current = "";
        lastCursorPos.current = textArea.selectionStart = textArea.selectionEnd = 0;
        textArea.focus();
        handleTextChange();
        postWorkerMessage({ type: "clrRes" /* RequestType.CLEAR_RESULT */, uid: nextUid() });
    }, [handleTextChange]);
    const calculate = useCallback(() => {
        if (lastCalculateType.current !== null && !resultNotReady) {
            const textArea = textAreaRef.current;
            textArea.value = textAreaValueForRender.current = result;
            textArea.selectionStart = textArea.selectionEnd = lastCursorPos.current = result.length;
            textArea.focus();
        }
        else {
            startCalculation("evaluate" /* RequestType.EVALUATE */, 1 /* CalcHistoryOption.REPLACE_HISTORY */);
        }
    }, [startCalculation, resultNotReady]);
    const simplify = useCallback(() => startCalculation("simplify" /* RequestType.SIMPLIFY */, 1 /* CalcHistoryOption.REPLACE_HISTORY */), [startCalculation]);
    const enterHistory = useCallback((item) => {
        nextUid();
        textAreaValueForRender.current = item[1];
        if (textAreaRef.current)
            textAreaRef.current.value = textAreaValueForRender.current;
        lastCursorPos.current = item[4] + 1;
        setResult(item[3]);
        setResultNotReady(item[5]);
        setNeedReplaceResult(false);
        setPage(2 /* Page.MAIN */);
    }, []);
    const clearHistory = useCallback(() => {
        historyItems.length = 0;
        setPage(2 /* Page.MAIN */);
    }, []);
    const undo = useCallback(() => {
        if (historyPtr.current > 0) {
            const newPtr = --historyPtr.current;
            enterHistory(historyItems[newPtr]);
        }
    }, []);
    const redo = useCallback(() => {
        if (historyPtr.current < historyItems.length - 1) {
            const newPtr = ++historyPtr.current;
            enterHistory(historyItems[newPtr]);
        }
    }, []);
    const aboutClick = useCallback(() => {
        setPage(3 /* Page.ABOUT_DIALOG */);
    }, []);
    const onSelectVar = useCallback((variable) => {
        variableToEnter.current = variable;
        setPage(2 /* Page.MAIN */);
    }, []);
    const onSelectFun = useCallback((fun) => {
        functionToEnter.current = fun;
        setPage(2 /* Page.MAIN */);
    }, []);
    const backToCalculator = useCallback(() => setPage(2 /* Page.MAIN */), []);
    const addMemory = useCallback(() => {
        postWorkerMessage({
            type: "addMem" /* RequestType.ADD_MEMORY */,
            uid: nextUid(),
            angleUnit: angleUnit,
            numeralBase: numeralBase,
        });
    }, [angleUnit, numeralBase]);
    const subMemory = useCallback(() => {
        postWorkerMessage({
            type: "subMem" /* RequestType.SUB_MEMORY */,
            uid: nextUid(),
            angleUnit: angleUnit,
            numeralBase: numeralBase,
        });
    }, [angleUnit, numeralBase]);
    const clearMemory = useCallback(() => {
        postWorkerMessageDelayed({
            type: "clrMem" /* RequestType.CLEAR_MEMORY */,
            uid: nextUid(),
            angleUnit: angleUnit,
            numeralBase: numeralBase,
        });
    }, [angleUnit, numeralBase]);
    const enterMemory = useCallback(() => {
        postWorkerMessageDelayed({
            type: "getMem" /* RequestType.GET_MEMORY */,
            uid: nextUid(),
            angleUnit: angleUnit,
            numeralBase: numeralBase,
        });
    }, [angleUnit, numeralBase]);
    useEffect(() => {
        if (textAreaRef.current && textAreaRef.current.value && lastCalculateType.current) {
            startCalculation(lastCalculateType.current, 1 /* CalcHistoryOption.REPLACE_HISTORY */);
        }
    }, [angleUnit, numeralBase, startCalculation]);
    useEffect(() => {
        document.addEventListener("selectionchange", onSelectionChange);
        return () => {
            document.removeEventListener("selectionchange", onSelectionChange);
        };
    }, []);
    const calculatorProps = {
        editor: (_jsx(CalculatorEditor, { readonly: workerRef.current === null, textRef: setTextRef, defaultValue: textAreaValueForRender.current, value: workerLoadError !== null ? "Error: " + workerLoadError : undefined, textOnBlur: useCallback(() => { textAreaHasFocus.current = false; }, []), textOnFocus: useCallback(() => { textAreaHasFocus.current = true; }, []), textOnInput: handleTextChange, angleUnit: angleUnit, numeralBase: numeralBase, setAngleUnit: setAngleUnit, setNumeralBase: setNumeralBase, openHistoryPage: useCallback(() => setPage(4 /* Page.HISTORY */), [setPage]), openAbout: aboutClick })),
        result: (_jsx(CalculatorResult, { textRef: resultTextRef, value: (workerRef.current === null && workerLoadError === null) ? "Loading..." : result, opacity: resultNotReady ? 0.5 : 1 })),
        buttonTextSize: fontSize,
        onNum: insertString,
        onPoint: useCallback(() => insertString("."), [insertString]),
        onOp: insertString,
        onVar: insertString,
        onFun: insertFun,
        onEqual: calculate,
        onSimplify: simplify,
        onErase: erase,
        onClear: clear,
        onBracket: insertString,
        onBracketPair: onBracketPair,
        onMoveCursor: moveCursor,
        onCopy: useCallback(() => copyText(result), [result]),
        onFavorite: aboutClick,
        onHistory: useCallback(() => { setNeedReplaceResult(false); setPage(4 /* Page.HISTORY */); }, [setPage]),
        onUndo: undo,
        onRedo: redo,
        onChooseVar: useCallback(() => setPage(5 /* Page.SELECT_VARIABLE */), [setPage]),
        onChooseFun: useCallback(() => setPage(6 /* Page.SELECT_FUNCTION */), [setPage]),
        supportMemory: true,
        onMemoryAdd: addMemory,
        onMemorySub: subMemory,
        onMemoryClear: clearMemory,
        onMemoryRequest: enterMemory,
    };
    switch (page) {
        case 4 /* Page.HISTORY */:
            return _jsx(CalculatorHistory, { historyItems: historyItems, enterHistory: enterHistory, clearHistory: clearHistory, exit: backToCalculator });
        case 5 /* Page.SELECT_VARIABLE */:
            return _jsx(CalculatorVariableSelect, { onVar: onSelectVar, exit: backToCalculator });
        case 6 /* Page.SELECT_FUNCTION */:
            return _jsx(CalculatorFunctionSelect, { onFun: onSelectFun, exit: backToCalculator });
    }
    return (_jsxs(_Fragment, { children: [createElement(landscape ? CalculatorLandscape : CalculatorPortrait, calculatorProps), _jsx(Snackbar, { open: snackbarMessage !== null, message: snackbarMessage, onClose: closeSnackbar, anchorOrigin: snackbarOrigin })] }));
});
const AboutDialog = memo(({ show, exit }) => {
    return (_jsxs(Dialog, { open: show, onClose: exit, "aria-labelledby": "alert-dialog-title", "aria-describedby": "alert-dialog-description", children: [_jsx(DialogTitle, { id: "alert-dialog-title", children: msgs[1 /* S.cpp_about */] }), _jsx(DialogContent, { children: _jsx(DialogContentText, { id: "alert-dialog-description", children: "Calculator++ Web v1.0.1" }) }), _jsx(DialogActions, { children: _jsx(Button, { onClick: exit, autoFocus: true, children: msgs[73 /* S.ok */] }) })] }));
});
export default memo(() => {
    const [currentPage, setCurrentPage] = useState(0 /* Page.INTRO */);
    const darkMode = useMediaQuery("(prefers-color-scheme:dark)");
    const theme = darkMode ? themeDark : themeLight;
    const [buttonSize, fontSize, landscape, setContainer] = useCalculatorSize();
    const onWorkerMessageRef = useRef(undefined);
    const onWorkerErrorRef = useRef(undefined);
    const workerState = useWorker(onWorkerMessageRef, onWorkerErrorRef);
    const historyItems = useMemo(() => [], []);
    const openWizard = useCallback(() => setCurrentPage(1 /* Page.WIZARD */), []);
    const openCalculator = useCallback(() => setCurrentPage(2 /* Page.MAIN */), []);
    let component;
    switch (currentPage) {
        case 0 /* Page.INTRO */:
            component = _jsx(CalculatorIntro, { exit: openWizard });
            break;
        case 1 /* Page.WIZARD */:
            component = _jsx(AppWizard, { buttonSize: buttonSize, fontSize: fontSize, setPage: setCurrentPage });
            break;
        case 2 /* Page.MAIN */:
        case 4 /* Page.HISTORY */:
        case 5 /* Page.SELECT_VARIABLE */:
        case 6 /* Page.SELECT_FUNCTION */:
        case 3 /* Page.ABOUT_DIALOG */:
            component = _jsx(Calculator, { landscape: landscape, fontSize: fontSize, workerState: workerState, workerMessageRef: onWorkerMessageRef, historyItems: historyItems, page: currentPage, setPage: setCurrentPage });
            break;
    }
    return (_jsxs(ThemeProvider, { theme: theme, children: [_jsx(CssBaseline, {}), _jsx(FilledBox, { ref: setContainer, sx: bgSx, children: component }), _jsx(AboutDialog, { show: currentPage === 3 /* Page.ABOUT_DIALOG */, exit: openCalculator })] }));
});
